import React, { useEffect, useRef, useState } from "react";
import Logo from "src/components/molecules/logo/Logo";
import { useLocation, useNavigate } from "react-router-dom";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import HyperLink from "src/components/atoms/hyper-link/hyper-link";
import ButtonPro from "src/components/atoms/primary-button/primary-button";
import { ErrorDisplay } from "src/components/atoms/error/error";
import { setNewPassword, signIn } from "src/components/organisms/auth";
import { ILoggedInUser, useAuth } from "./auth-context";
import { PermissionBagService } from "./permissionBagService";
import { TokenHelper } from "./token-helper";

const SetNewPassword = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const username = location.state?.businessEmail;
	const { cognitoUser, login } = useAuth();
	const [password, setPassword] = useState("");
	const [confirmNewPassword, setConfirmNewPassword] = useState("");
	const [error, setError] = useState("");
	const tokenHelper = useRef<TokenHelper | null>(null);
	const permissionBag = useRef<PermissionBagService | null>(null);

	useEffect(() => {
		if (!username || !cognitoUser) navigate("/login");
	}, []);

	const handleSubmit = async () => {
		if (password != confirmNewPassword) {
			setError("New password and Confirm new password do not match");
		}

		try {
			if (cognitoUser) {
				const success = await setNewPassword(cognitoUser, password);

				if (success) {
					const businessEmail = cognitoUser.getUsername();

					signIn(businessEmail, password, async (err, result) => {
						if (err) {
							if (err.code === "UserNotConfirmedException") {
								navigate("/verify", {
									state: { businessEmail, password },
								});
							} else {
								setError(err.message || JSON.stringify(err));
							}
						} else {
							// Decode token
							const tokenProps =
								await tokenHelper.current?.decodeToken(
									result.accessToken.jwtToken
								);

							permissionBag.current = new PermissionBagService(
								result.accessToken.jwtToken
							);

							const permissions =
								await permissionBag.current.getPermissionBag(
									result.accessToken.payload.role
								);

							if (!permissions?.permissions.length) {
								navigate("/error", {
									state: {
										error: "Unable to get user permissions",
									},
								});
								setError("Unable to get user permissions");
								return;
							}

							const loggedInUser: ILoggedInUser = {
								id: result.idToken.payload.sub,
								givenName: result.idToken.payload.given_name,
								familyName: result.idToken.payload.family_name,
								username: businessEmail,
								phoneNumber:
									result.idToken.payload.phone_number,
								ex_user_id:
									result.accessToken.payload.ex_user_id,
								accessToken: result.accessToken.jwtToken,
								num: result.accessToken.payload.num,
								accountId: tokenProps?.account_id ?? "",
								email: tokenProps?.email ?? "",
								role: tokenProps?.role,
								exp: result.accessToken.payload.exp,
								permissionBag: permissions || {
									permissions: [],
								},
								has_multiple_accounts:
									result.accessToken.payload
										.has_multiple_accounts,
							};

							login(loggedInUser);

							if (!loggedInUser.accountId) {
								navigate("/select-account");
							} else {
								navigate("/");
							}
						}
					});
				}
			} else {
				setError("User session not available.");
			}
		} catch (error) {
			console.error("Failed to set new password:", error);
			setError("Failed to update password. Please try again.");
		}
	};

	return (
		<>
			<div className="bg-gradient-to-r from-gray-700 to-gray-900 flex flex-col items-center justify-start md:justify-center h-screen w-screen py-8">
				<div className="min-w-[600px] bg-white text-gray-900 flex flex-col gap-6 items-center justify-start md:justify-center h-fit w-fit p-12 md:shadow-[0_30px_50px_rgba(0,0,0,.5)]">
					<Logo></Logo>
					<span className="font-Poppins font-semibold text-md">
						Set new password for
					</span>
					<span className="font-Poppins font-semibold text-md">
						{username}
					</span>
					<InputBox
						label={"New password"}
						type={"password"}
						value={password}
						onChange={function (value: string): void {
							setPassword(value);
						}}
					></InputBox>
					<InputBox
						label={"Confirm new password"}
						type={"password"}
						value={confirmNewPassword}
						onChange={function (value: string): void {
							setConfirmNewPassword(value);
						}}
					></InputBox>
					<ButtonPro
						label={"Change"}
						onClick={handleSubmit}
						disabled={!username || !password || !confirmNewPassword}
					></ButtonPro>
					<HyperLink
						href={"/login"}
						label={"<- back to login"}
					></HyperLink>

					{error && <ErrorDisplay error={error}></ErrorDisplay>}
				</div>
			</div>
		</>
	);
};

export default SetNewPassword;
